import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Phone, KeyRound, ChevronLeft, Send, MessageCircle, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { splitPhone, joinPhone, COUNTRY_CODES } from "../lib/phone";
import LegalFooter from "../components/LegalFooter";

/**
 * Reusable phone+OTP login screen for tenant_admin / super_admin / seller.
 * Customer & Delivery already have their own (mobile-themed) flows.
 */
export default function PhoneOtpLogin({
  role,                       // "tenant_admin" | "super_admin" | "seller"
  altRole,                    // optional 2nd role to try on the same phone (e.g. "supervisor" alongside "tenant_admin")
  title,                      // e.g. "Tenant Admin"
  subtitle = "Operator Console",
  icon: Icon,                 // lucide icon component
  accentClass = "bg-brand-sky",
  buttonClass = "bg-brand-sky hover:bg-blue-700",
  channelActiveClass = "border-brand-sky bg-brand-sky/10 text-brand-sky",
  defaultPhone = "",
  tenantSlug = "",            // optional — only needed for tenant_admin / seller
  redirectTo,                 // where to nav after successful login
  dark = false,               // super-admin uses a dark theme
  testIdPrefix = "phone-otp",
}) {
  const nav = useNavigate();
  const { login } = useAuth();
  const init = splitPhone(defaultPhone);
  const [step, setStep] = useState("phone");
  const [cc, setCc] = useState(init.cc);
  const [local, setLocal] = useState(init.local);
  const phone = joinPhone(cc, local);
  const [otp, setOtp] = useState("");
  const [channel, setChannel] = useState("sms");
  const [loading, setLoading] = useState(false);
  const [resendSecs, setResendSecs] = useState(0);

  useEffect(() => {
    if (resendSecs <= 0) return undefined;
    const id = setInterval(() => setResendSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendSecs]);

  const sendOtp = async (overrideChannel) => {
    const digits = local.replace(/\D/g, "");
    if (digits.length < 10) {
      toast.error("Enter a 10-digit phone number"); return;
    }
    const useChannel = overrideChannel || channel;
    setLoading(true);
    try {
      const { data } = await api.post("/api/auth/otp/request",
        { phone, role, channel: useChannel, tenant_slug: tenantSlug || undefined });
      toast.success(data.message || `OTP sent via ${useChannel.toUpperCase()}`);
      setChannel(useChannel);
      setStep("otp");
      setResendSecs(data.resend_after || 30);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const resendOtp = (resendChannel) => {
    if (resendSecs > 0) return;
    setOtp("");
    sendOtp(resendChannel);
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      let data;
      try {
        ({ data } = await api.post("/api/auth/otp/verify",
          { phone, otp, role, tenant_slug: tenantSlug || undefined }));
      } catch (e) {
        // OTP codes are stored per-phone, not per-role, so a phone with no
        // `role` account (but an `altRole` account, e.g. supervisor instead
        // of tenant_admin) can still verify against altRole with the same
        // code once the primary role lookup 403s.
        if (altRole && e?.response?.status === 403) {
          ({ data } = await api.post("/api/auth/otp/verify",
            { phone, otp, role: altRole, tenant_slug: tenantSlug || undefined }));
        } else {
          throw e;
        }
      }
      if (data.user.role !== role && data.user.role !== altRole) {
        toast.error(`Not a ${title.toLowerCase()} account`); return;
      }
      login(data.token, data.user, data.tenant || null, data.refresh_token || null);
      toast.success("Welcome!");
      nav(redirectTo);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setLoading(false); }
  };

  return (
    <div className={`min-h-screen flex flex-col ${dark ? "bg-brand-ink text-white" : "bg-brand-cream"}`}
         data-testid={`${testIdPrefix}-page`}>
      <div className="p-5">
        <Link to="/" className={`inline-flex items-center gap-1 ${dark ? "text-white/70" : "text-neutral-600"}`} data-testid="back-link">
          <ChevronLeft className="w-4 h-4" /> Home
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className={`inline-flex w-16 h-16 rounded-2xl items-center justify-center mb-4 ${accentClass}`}>
              {Icon && <Icon className="w-8 h-8 text-white" strokeWidth={2.5} />}
            </div>
            <div className="font-heading font-extrabold text-2xl tracking-tight">{title}</div>
            <div className={`text-xs uppercase tracking-wider font-bold mt-1 ${dark ? "text-white/60" : "text-neutral-500"}`}>
              {subtitle}
            </div>
          </div>

          <div className={`rounded-3xl p-7 ${dark ? "bg-white text-brand-ink" : "bg-white border border-neutral-200"}`}>
            {step === "phone" ? (
              <>
                <h2 className="font-heading text-2xl font-bold mb-1">Sign in with phone</h2>
                <p className="text-neutral-600 text-sm mb-6">We&rsquo;ll send a 6-digit code via SMS or WhatsApp.</p>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Phone number</label>
                <div className="flex gap-2 mt-2">
                  <div className="relative">
                    <select value={cc} onChange={(e) => setCc(e.target.value)}
                            className="appearance-none h-14 pl-3 pr-7 rounded-2xl border border-neutral-200 bg-white font-mono font-bold text-base cursor-pointer"
                            data-testid={`${testIdPrefix}-cc`}
                            aria-label="Country code">
                      {COUNTRY_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-400 pointer-events-none" />
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                    <Input type="tel" inputMode="numeric" maxLength={10}
                           value={local}
                           onChange={(e) => setLocal(e.target.value.replace(/\D/g, "").slice(0, 10))}
                           className="h-14 pl-12 rounded-2xl text-base font-mono tracking-wider" placeholder="98765 43210"
                           data-testid={`${testIdPrefix}-phone`} />
                  </div>
                </div>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 mt-5 block">Channel</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => setChannel("sms")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "sms" ? channelActiveClass : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid={`${testIdPrefix}-channel-sms`}>
                    <Send className="w-4 h-4" /> SMS
                  </button>
                  <button onClick={() => setChannel("whatsapp")}
                          className={`h-14 rounded-2xl font-bold text-sm border-2 inline-flex items-center justify-center gap-2 ${channel === "whatsapp" ? "border-brand-leaf bg-brand-leaf/10 text-brand-leaf" : "border-neutral-200 bg-white text-neutral-700"}`}
                          data-testid={`${testIdPrefix}-channel-whatsapp`}>
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </button>
                </div>

                <Button onClick={() => sendOtp()} disabled={loading}
                        className={`w-full mt-6 h-14 rounded-2xl font-bold text-base ${buttonClass}`}
                        data-testid={`${testIdPrefix}-send-otp`}>
                  {loading ? "Sending..." : `Send OTP via ${channel.toUpperCase()}`}
                </Button>
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold mb-1">Enter OTP</h2>
                <p className="text-neutral-600 text-sm mb-6">Sent via <b className="capitalize">{channel}</b> to <b>{cc} {local}</b></p>

                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">6-digit OTP</label>
                <div className="relative mt-2">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <Input type="text" inputMode="numeric" maxLength={6} value={otp}
                         onChange={(e) => setOtp(e.target.value)}
                         className="h-14 pl-12 rounded-2xl text-base font-mono tracking-[0.4em]"
                         placeholder="123456" data-testid={`${testIdPrefix}-otp`} />
                </div>

                <Button onClick={verifyOtp} disabled={loading || otp.length < 4}
                        className={`w-full mt-6 h-14 rounded-2xl font-bold text-base ${buttonClass}`}
                        data-testid={`${testIdPrefix}-verify`}>
                  {loading ? "Verifying..." : "Verify & Continue"}
                </Button>

                <div className="mt-5 pt-4 border-t border-neutral-100" data-testid={`${testIdPrefix}-resend-block`}>
                  {resendSecs > 0 ? (
                    <div className="text-center text-sm text-neutral-500" data-testid={`${testIdPrefix}-resend-countdown`}>
                      Resend available in <span className="font-bold text-neutral-700">{resendSecs}s</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-center text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">
                        Didn&rsquo;t get it? Resend via
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => resendOtp("sms")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-sky text-brand-sky inline-flex items-center justify-center gap-2 hover:bg-brand-sky/10 disabled:opacity-50"
                                data-testid={`${testIdPrefix}-resend-sms`}>
                          <Send className="w-4 h-4" /> SMS
                        </button>
                        <button onClick={() => resendOtp("whatsapp")} disabled={loading}
                                className="h-11 rounded-xl font-bold text-sm border-2 border-brand-leaf text-brand-leaf inline-flex items-center justify-center gap-2 hover:bg-brand-leaf/10 disabled:opacity-50"
                                data-testid={`${testIdPrefix}-resend-whatsapp`}>
                          <MessageCircle className="w-4 h-4" /> WhatsApp
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <button onClick={() => { setStep("phone"); setResendSecs(0); }}
                        className="w-full mt-4 text-sm text-neutral-600 hover:text-brand-sky"
                        data-testid={`${testIdPrefix}-change-phone`}>
                  Change phone / channel
                </button>
              </>
            )}
          </div>
          {/* Footer stays in-column so it stacks below the form on mobile */}
          <LegalFooter theme="light"/>
        </div>
      </div>
    </div>
  );
}
